import React, { useState, useMemo, useEffect } from 'react';
import Navbar from '../../../../../components/navbar.jsx';
import Footer from '../../../../../components/footer.jsx';
import Button from '../../../../../components/Button.jsx';
import Pagination from '../../../../../components/Pagination.jsx';
import DatePicker from '../../../../../components/DatePicker.jsx';
import { ReportsSidebar, ReportsTable } from '../../components/index.js';
import { medAidApi } from '../../../../../api/dispensary/reports/MedicalAidStats/MedAidApi.js';
import { getBranchByCode } from '../../../../../api/branchManagement]/branchApi.js';
import { useAuth } from '../../../../../context/AuthContext.jsx';
import PrintReport from '../../components/PrintReport.jsx';
import styles from '../css/MedicalAidStatistics.module.css';

const MedicalAidStatistics = () => {
  // Get user authentication data
  const { user } = useAuth();
  
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [medAidStats, setMedAidStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [branchInfo, setBranchInfo] = useState(null);

  // Max date set to today to prevent future date selection
  const maxDate = new Date();

  /**
   * useEffect hook to fetch branch information when component mounts
   * This runs once on component load to get pharmacy details for PDF export
   */
  useEffect(() => {
    /**
     * Async function to fetch branch information from the backend API
     * Uses the user's branchcode to get specific branch details
     */
    const fetchBranchInfo = async () => {
      try {
        if (user?.branchcode) {
          const response = await getBranchByCode(user.branchcode);
          if (response.success) {
            setBranchInfo(response.data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch branch info:', error);
        // Set default branch info if API fails
        setBranchInfo({
          branchname: 'DURBAN NORTH PHARMACY',
          address: '123 Umhlanga Rocks Drive, Durban North, 4051',
          telephoneno: '(031) 123-4567'
        });
      }
    };

    // Execute the fetch function
    fetchBranchInfo();
  }, [user?.branchcode]);

  /**
   * useEffect hook to fetch medical aid statistics when component mounts or dates change
   * This runs once on component load and whenever fromDate or toDate changes
   */
  useEffect(() => {
    /**
     * Async function to fetch medical aid statistics from the backend API
     * Handles loading states, data processing, and error handling
     * Accepts optional date parameters for filtering
     */
    const fetchMedAidStats = async () => {
      try {
        // Set loading state to true to show loading indicator
        setLoading(true);
        
        // Format dates for API (convert to ISO string format)
        const formattedFromDate = fromDate ? fromDate.toISOString().split('T')[0] : null;
        const formattedToDate = toDate ? toDate.toISOString().split('T')[0] : null;
        
        // Make API call to fetch medical aid statistics with date filters
        const response = await medAidApi.getAcuteChronicStats(formattedFromDate, formattedToDate);
        
        // Check if API response was successful and contains data
        if (response.success) {
          // Store the medical aid statistics data in component state
          setMedAidStats(response.data);
        }
      } catch (error) {
        // Log any errors that occur during API call
        console.error('Failed to fetch medical aid stats:', error);
      } finally {
        // Always set loading to false when operation completes
        setLoading(false);
      }
    };

    // Execute the fetch function
    fetchMedAidStats();
  }, [fromDate, toDate]); // Dependency array includes dates to refetch when they change

  /**
   * useMemo hook to optimize performance
   * Creates a memoized version of the medAidStats array
   * Maps the API data to match the table structure
   */
  const rows = useMemo(() => {
    // If there's actual backend data, use it
    if (medAidStats && medAidStats.length > 0) {
      return medAidStats.map(item => ({
        medAid: '',
        medAidName: item.type,
        scripts: item.scripts,
        gross: item.gross,
        income: item.income,
        cost: '0.00', // Set as string to match fallback data format
        aveIncome: item.scripts > 0 ? (parseFloat(item.income) / item.scripts).toFixed(2) : '0.00',
        percentCount: `${item.percent}%`,
        isTotal: item.type === 'Grand Total'
      }));
    }
    
    // Fallback data when no backend data is available
    return [
      {
        medAid: '',
        medAidName: 'Acute Total',
        scripts: 45,
        gross: '2250.00',
        income: '2250.00',
        cost: '0.00',
        aveIncome: '50.00',
        percentCount: '60.0%',
        isTotal: false
      },
      {
        medAid: '',
        medAidName: 'Chronic Total',
        scripts: 30,
        gross: '1800.00',
        income: '1800.00',
        cost: '0.00',
        aveIncome: '60.00',
        percentCount: '40.0%',
        isTotal: false
      },
      {
        medAid: '',
        medAidName: 'Grand Total',
        scripts: 75,
        gross: '4050.00',
        income: '4050.00',
        cost: '0.00',
        aveIncome: '54.00',
        percentCount: '100.0%',
        isTotal: true
      }
    ];
  }, [medAidStats]);

  const columns = useMemo(
    () => [
      { header: 'Medical Aid', accessor: 'medAid' },
      { header: 'Medical Aid Name', accessor: 'medAidName' },
      { header: 'No. of Scripts', accessor: 'scripts' },
      { header: 'Gross', accessor: 'gross' },
      { header: 'Income', accessor: 'income' },
      { header: 'Cost', accessor: 'cost' },
      { header: 'Ave.Income', accessor: 'aveIncome' },
      { header: '%Count', accessor: 'percentCount' },
    ],
    []
  );

  const rowsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(rows.length / rowsPerPage));
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (safeCurrentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageRows = rows.slice(startIndex, endIndex);

  const handlePrint = () => {
    PrintReport({
      reportType: 'medicalAid',
      reportTitle: 'MEDICAL AID STATISTICS REPORT',
      branchInfo,
      headers: ['Medical Aid', 'Medical Aid Name', 'No. of Scripts', 'Gross', 'Income', 'Cost', 'Ave.Income', '%Count'],
      data: rows, // Use all rows for print (not just current page)
      dateRange: { fromDate, toDate },
      isPrint: true
    });
  };

  const handleExport = () => {
    PrintReport({
      reportType: 'medicalAid',
      reportTitle: 'MEDICAL AID STATISTICS REPORT',
      branchInfo,
      headers: ['Medical Aid', 'Medical Aid Name', 'No. of Scripts', 'Gross', 'Income', 'Cost', 'Ave.Income', '%Count'],
      data: rows, // Use all rows for export (not just current page)
      dateRange: { fromDate, toDate },
      isPrint: false
    });
  };

  return (
    <div className={styles.container}>
      <Navbar backTo="/customer/dispensary" backReplace />
      <div className={styles.mainContent}>
        <div className={styles.layout}>
          <div className={styles.sidebarContainer}>
            <ReportsSidebar />
          </div>

          <div className={styles.content}>
            <div className={styles.reportContainer}>
              <div className={styles.dateFiltersSection}>
                <div className={styles.dateFilters}>
                  <div className={styles.dateFilter}>
                    <label className={styles.dateLabel}>From</label>
                    <DatePicker
                      selectedDate={fromDate}
                      onDateChange={setFromDate}
                      placeholder=""
                      className={styles.dateInput}
                      maxDate={maxDate}
                      disableFutureDates={true}
                    />
                  </div>

                  <div className={styles.dateFilter}>
                    <label className={styles.dateLabel}>To</label>
                    <DatePicker
                      selectedDate={toDate}
                      onDateChange={setToDate}
                      placeholder=""
                      className={styles.dateInput}
                      maxDate={maxDate}
                      disableFutureDates={true}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.actionButtonsSection}>
                <div className={styles.actionButtons}>
                  <Button onClick={handlePrint} variant="approve" className={styles.printButton}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9V2h12v7" />
                      <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
                      <rect x="6" y="14" width="12" height="8" />
                    </svg>
                    Print
                  </Button>

                  <Button onClick={handleExport} variant="primary" className={styles.exportButton}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <polyline points="7,10 12,15 17,10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Export
                  </Button>
                </div>
              </div>

              <div className={styles.noticeRow}>
                <span className={styles.noticeText}>Note: Disc includes P.O.S Disc</span>
              </div>

              <div className={styles.tableWrapper}>
                {loading ? (
                  <div className={styles.loadingContainer}>
                    Loading medical aid statistics...
                  </div>
                ) : (
                  <ReportsTable 
                    columns={columns} 
                    data={pageRows}
                    striped={true}
                  />
                )}
              </div>

              <div className={styles.paginationContainer}>
                <Pagination
                  currentPage={safeCurrentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MedicalAidStatistics;
